import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { getMySubscription, toggleSubscription } from "@/lib/forum-extra.functions";
import { useAuth } from "@/hooks/use-auth";

type Props = {
  postId?: string;
  categoryId?: string;
  label?: string;
  className?: string;
};

export function FollowButton({ postId, categoryId, label = "Bevaka", className = "" }: Props) {
  const { signedIn } = useAuth();
  const getSub = useServerFn(getMySubscription);
  const toggle = useServerFn(toggleSubscription);
  const qc = useQueryClient();
  const key = ["forum", "subscription", postId ?? categoryId ?? "none"];

  const { data } = useQuery({
    queryKey: key,
    enabled: signedIn && Boolean(postId || categoryId),
    queryFn: () => getSub({ data: { post_id: postId, category_id: categoryId } }),
  });

  const following = postId ? Boolean(data?.thread) : Boolean(data?.category);

  const mutation = useMutation({
    mutationFn: () => toggle({ data: { post_id: postId, category_id: categoryId } }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: key });
      toast.success(res.following ? "Du bevakar nu – du får notis vid nya svar" : "Bevakning avslutad");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Kunde inte spara"),
  });

  if (!signedIn) {
    return (
      <a
        href="/login"
        className={`inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition hover:bg-secondary ${className}`}
      >
        <Bell size={14} /> {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
      aria-pressed={following}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${
        following
          ? "border-accent bg-accent/10 text-accent"
          : "border-border bg-card hover:bg-secondary"
      } ${className}`}
    >
      {following ? <BellRing size={14} /> : <Bell size={14} />}
      {following ? "Bevakas" : label}
    </button>
  );
}
